import React from "react";
import { observer } from "mobx-react";
import ReactApexChart from "react-apexcharts";
import { baseChartOptions, axisLabelStyle, sequentialColors } from "../visuals/chartTheme";
import { getNiceMax } from "Pages/Dashboard/dashboard/DashboardV2/utils/chartDataUtils";
import { formatMetric } from "../visuals/formatMetric";
import { useTokenHost } from "../visuals/cssVar";

/**
 * Magnitude across a two-dimensional grid — day × hour send times, cohort
 * retention. Sequential ramp only: a categorical ramp here would imply the
 * cells belong to different kinds of thing rather than differing in amount.
 *
 * Below roughly 5×5 a table reads better, so small grids are refused.
 */
const MIN_CELLS = 20;

const HeatmapBlock = observer(({ block }) => {
  // `ready` forces one re-render after mount so token reads see a live ref.
  const [rootRef, ready] = useTokenHost();
  const rows = block.rows || [];
  const cellCount = rows.reduce((sum, row) => sum + (row.values || []).length, 0);
  if (!rows.length || cellCount < MIN_CELLS) return null;

  const ramp = sequentialColors(rootRef.current);
  const all = rows.flatMap(row => (row.values || []).map(cell => Number(cell.value) || 0));
  // Round the ramp's top to the same "nice" bound the dashboard uses, so the
  // colour bands fall on readable numbers rather than on 22.4/6.
  const { max } = getNiceMax(Math.max(...all, 1));
  const step = max / ramp.length;

  const base = baseChartOptions(rootRef.current);
  const options = {
    ...base,
    chart: { ...base.chart, type: "heatmap" },
    grid: { ...base.grid, yaxis: { lines: { show: false } } },
    stroke: { width: 2, colors: ["transparent"] },
    plotOptions: {
      heatmap: {
        radius: 4,
        enableShades: false,
        useFillColorAsStroke: false,
        colorScale: {
          ranges: ramp.map((color, index) => ({
            from: index === 0 ? 0 : step * index,
            to: step * (index + 1),
            color
          }))
        }
      }
    },
    xaxis: {
      type: "category",
      labels: axisLabelStyle(rootRef.current),
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: axisLabelStyle(rootRef.current) },
    tooltip: { y: { formatter: value => formatMetric(value, { unit: block.unit || "" }) } }
  };

  const series = rows.map(row => ({
    name: row.label,
    data: (row.values || []).map(cell => ({ x: cell.label, y: Number(cell.value) || 0 }))
  }));

  return (
    <div className="cpVisual" ref={rootRef}>
      {block.title && <div className="cpVisual__title">{block.title}</div>}
      <ReactApexChart key={ready ? "themed" : "initial"} options={options} series={series} type="heatmap" height={Math.max(200, rows.length * 44)} />
    </div>
  );
});

export default HeatmapBlock;
